/**
 * useAmbientDucking — hook utilitaire pour signaler à la musique d'ambiance
 * qu'une voix ou une saisie est en cours. La musique baisse automatiquement
 * et remonte une fois la raison levée.
 *
 * Usage :
 *   const duck = useAmbientDucking("voice-ai");
 *   useEffect(() => {
 *     if (isCharlotteSpeaking) duck.engage(0.2);  // 20 % du volume nominal
 *     else duck.release();
 *   }, [isCharlotteSpeaking]);
 *
 * Plusieurs composants peuvent demander un ducking simultanément — la musique
 * ne remonte qu'une fois toutes les raisons levées.
 */
import { useCallback, useEffect, useRef } from "react";
import { useAmbientSound } from "@/context/AmbientSoundContext";

export function useAmbientDucking(reason) {
  const { requestDuck, releaseDuck } = useAmbientSound();
  const engagedRef = useRef(false);

  const engage = useCallback((multiplier = 0.25) => {
    if (!engagedRef.current) {
      engagedRef.current = true;
      requestDuck(reason, multiplier);
    }
  }, [requestDuck, reason]);

  const release = useCallback(() => {
    if (engagedRef.current) {
      engagedRef.current = false;
      releaseDuck(reason);
    }
  }, [releaseDuck, reason]);

  // Nettoyage automatique au démontage
  useEffect(() => {
    return () => {
      if (engagedRef.current) {
        releaseDuck(reason);
        engagedRef.current = false;
      }
    };
  }, [releaseDuck, reason]);

  return { engage, release };
}

/**
 * useAmbientScene — déclare une scène UX courante (welcome / processing /
 * upload / transfer / hold). N'est utilisée que pour de la télémétrie et
 * pour permettre à l'AmbientSoundWidget d'afficher éventuellement un libellé
 * (« CONFILEX vous accompagne… ») pendant les longs traitements.
 */
export function useAmbientScene(scene) {
  const { setCurrentScene } = useAmbientSound();
  useEffect(() => {
    if (scene) {
      setCurrentScene(scene);
      return () => setCurrentScene(null);
    }
    return undefined;
  }, [scene, setCurrentScene]);
}
